import React from 'react';

/* ── helpers ─────────────────────────────────────────────────────────── */

const Topline: React.FC<{ title: string; page: string }> = ({ title, page }) => (
  <div className="flex items-center justify-between gap-5 mb-7 pb-3.5 border-b border-[#e7eaf0]">
    <div className="text-[13px] text-[#667085] font-extrabold">{title}</div>
    <div className="text-[#98a2b3] text-xs">{page}</div>
  </div>
);

const Section: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-8 last:mb-0">{children}</div>
);

const H2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="text-[27px] mb-3.5 text-[#101828] font-bold tracking-tight leading-tight">{children}</h2>
);

const Quote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="my-5 px-6 py-5 rounded-3xl bg-gradient-to-br from-[#f5f3ff] to-[#eef2ff] border border-[#ddd6fe] text-[#3b2b77] text-lg font-black leading-relaxed">
    {children}
  </div>
);

const InfoCard: React.FC<{ title: string; children: React.ReactNode; soft?: boolean }> = ({
  title,
  children,
  soft,
}) => (
  <div
    className={`p-[18px] rounded-3xl border border-[#e7eaf0] ${
      soft ? 'bg-[#f8fafc]' : 'bg-white shadow-[0_10px_30px_rgba(15,23,42,0.045)]'
    }`}
  >
    <strong className="block mb-1.5 text-base text-[#101828]">{title}</strong>
    <p className="m-0 text-[#4b5563] text-[13.8px] leading-relaxed">{children}</p>
  </div>
);

const Cards: React.FC<{ children: React.ReactNode; cols?: 2 | 3 }> = ({ children, cols = 2 }) => (
  <div
    className={`grid gap-3.5 mt-4 ${
      cols === 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'
    }`}
  >
    {children}
  </div>
);

const Metric: React.FC<{ num: string; title: string; desc: string }> = ({ num, title, desc }) => (
  <div className="px-3.5 py-4 rounded-[22px] bg-gradient-to-b from-white to-[#f9fafb] border border-[#e7eaf0] text-center min-h-[145px]">
    <div className="w-11 h-11 mx-auto mb-2.5 rounded-2xl bg-[#ede9fe] text-[#7c3aed] grid place-items-center font-black text-lg">
      {num}
    </div>
    <strong className="block mb-1 text-[14.5px] text-[#101828]">{title}</strong>
    <span className="block text-[#667085] text-[12.2px] leading-relaxed">{desc}</span>
  </div>
);

const Step: React.FC<{ num: number; title: string; children: React.ReactNode }> = ({
  num,
  title,
  children,
}) => (
  <div className="relative pr-[72px] pl-[18px] py-[17px] rounded-[22px] bg-white border border-[#e7eaf0] shadow-[0_8px_24px_rgba(15,23,42,0.04)]">
    <div className="absolute top-[17px] right-[18px] w-[38px] h-[38px] rounded-[14px] grid place-items-center bg-gradient-to-br from-[#7c3aed] to-[#4f46e5] text-white font-black text-sm">
      {num}
    </div>
    <strong className="block mb-1 text-[#101828]">{title}</strong>
    <p className="m-0 text-[#4b5563] text-sm leading-relaxed">{children}</p>
  </div>
);

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-[18px] py-4 rounded-[20px] bg-[#fffbeb] border border-[#fde68a] text-[#92400e] font-extrabold text-sm leading-relaxed">
    {children}
  </div>
);

const ContentPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section className="bg-white rounded-none sm:rounded-sm shadow-[0_18px_60px_rgba(17,24,39,0.16)] overflow-hidden">
    <div className="px-6 sm:px-12 py-8 sm:py-10">{children}</div>
  </section>
);

/* ── main content ────────────────────────────────────────────────────── */

const MarketPulseProductDocContent: React.FC = () => (
  <div dir="rtl" className="text-[#111827] text-[14.7px] leading-[1.85] space-y-4 sm:space-y-5">
    {/* ── Cover ── */}
    <section
      className="relative overflow-hidden text-white shadow-[0_18px_60px_rgba(17,24,39,0.16)] rounded-none sm:rounded-sm"
      style={{
        background:
          'radial-gradient(circle at 14% 18%, rgba(255,255,255,0.18), transparent 26%), radial-gradient(circle at 78% 72%, rgba(124,58,237,0.42), transparent 28%), linear-gradient(135deg, #0f172a 0%, #251157 42%, #6d28d9 100%)',
      }}
    >
      <div className="flex flex-col justify-between min-h-0 sm:min-h-[480px] px-6 sm:px-12 py-8 sm:py-14">
        <div>
          <div className="max-w-[710px] mt-10 sm:mt-14">
            <h1 className="m-0 text-4xl sm:text-[56px] font-black tracking-tighter leading-[1.15]">نبض بازار</h1>
            <div className="mt-4 sm:mt-[18px] text-xl sm:text-2xl text-[#f4f1ff] font-bold">
              اولین گام برای دیدن جایگاه واقعی کافه و رستوران شما در بازار
            </div>
            <p className="mt-5 sm:mt-6 text-base sm:text-lg text-[#e9dfff] max-w-[650px] leading-relaxed">
              نبض بازار عملکرد کسب‌وکار شما را بر پایه داده واقعی صندوق فروشگاهی با میانگین بازار
              کسب‌وکارهای متصل مقایسه می‌کند؛ تا بدانید در فروش، سفارش، قیمت و سبد خرید جلوتر از
              بازار هستید یا عقب‌تر.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mt-8 sm:mt-10">
              {[
                {
                  title: 'فعال برای مشتریان',
                  desc: 'به‌عنوان ارزش افزوده برای مشتریان اسمارت‌ایکس فعال شده است.',
                },
                {
                  title: 'محرمانه و تجمیعی',
                  desc: 'هیچ اطلاعاتی از هیچ کسب‌وکاری به‌صورت جداگانه نمایش داده نمی‌شود.',
                },
                {
                  title: 'آماده توسعه',
                  desc: 'مسیر آینده: تحلیل عمیق، دستیار هوشمند، پیش‌بینی و پیشنهاد اقدام.',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="min-h-[116px] p-[18px] rounded-3xl bg-white/10 border border-white/18 backdrop-blur-md"
                >
                  <strong className="block mb-1.5 text-lg text-white">{item.title}</strong>
                  <span className="block text-[#e9d5ff] text-[13px] leading-relaxed">{item.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mt-10 text-[#ddd6fe] text-[13px]">
          <div>نبض بازار؛ ببینید در کجای بازار ایستاده اید.</div>
        </div>
      </div>
    </section>

    {/* ── Page 01 ── */}
    <ContentPage>
      <Topline title="نبض بازار | معرفی محصول" page="01" />
      <Section>
        <H2>نبض بازار چیست؟</H2>
        <p className="text-[17px] text-[#344054] leading-relaxed">
          نبض بازار یک پنل مقایسه‌ای در اسمارت‌ایکس است که نشان می‌دهد عملکرد کافه یا رستوران شما
          نسبت به میانگین  کسب‌وکارهای متصل بازار  چگونه است.
        </p>
        <p className="text-[#4b5563] leading-relaxed">
          این پنل فقط عددهای فروش شما را نمایش نمی‌دهد؛ بلکه به عددها زمینه می‌دهد. یعنی اگر فروش
          شما رشد کرده یا افت کرده، مشخص می‌کند این تغییر نسبت به بازار چه معنایی دارد.
        </p>
        <Quote>
          در بازار بی‌ثبات، عددهای خودتان به‌تنهایی کافی نیستند؛ باید بدانید بازار در همان زمان چه
          مسیری رفته است.
        </Quote>
      </Section>

      <Section>
        <H2>مسئله‌ای که حل می‌کند</H2>
        <p className="text-[#4b5563] leading-relaxed">
          مدیر کافه یا رستوران معمولاً می‌داند فروشش نسبت به ماه قبل چقدر تغییر کرده، اما نمی‌داند
          این تغییر خوب است یا بد. چون ممکن است بازار همزمان رشد کرده، افت کرده یا تحت فشار تورم و
          تغییر قدرت خرید بوده باشد.
        </p>
        <Cards>
          <InfoCard title="آیا مشکل از من است؟">
            اگر فروش افت کرده، نبض بازار نشان می‌دهد افت شما بیشتر از بازار بوده یا کمتر.
          </InfoCard>
          <InfoCard title="آیا رشدم واقعی است؟">
            اگر فروش رشد کرده، مشخص می‌شود این رشد از بازار جلوتر بوده یا عقب‌تر.
          </InfoCard>
          <InfoCard title="آیا قیمت‌گذاری درست است؟">
            رشد قیمت شما در کنار رشد قیمت بازار دیده می‌شود.
          </InfoCard>
          <InfoCard title="آیا سبد خرید مشکل دارد؟">
            می‌توانید بفهمید مشتریان در هر سفارش نسبت به بازار بیشتر خرج می‌کنند یا کمتر.
          </InfoCard>
        </Cards>
      </Section>

      <Section>
        <H2>نبض بازار چه چیزی ارائه می‌دهد؟</H2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          <Metric num="۱" title="رشد فروش" desc="مقایسه تغییر میزان فروش شما با بازار" />
          <Metric num="۲" title="رشد سفارش" desc="مقایسه تغییر تعداد سفارش‌ها با بازار" />
          <Metric num="۳" title="رشد قیمت" desc="مقایسه تغییر سطح قیمت آیتم‌های فروخته‌شده" />
          <Metric num="۴" title="رشد سبد" desc="مقایسه تغییر میانگین ارزش خرید یا سفارش" />
        </div>
      </Section>
    </ContentPage>

    {/* ── Page 02 ── */}
    <ContentPage>
      <Topline title="نبض بازار | نحوه کار" page="02" />
      <Section>
        <H2>نبض بازار چگونه کار می‌کند؟</H2>
        <div className="grid gap-3 mt-4">
          <Step num={1} title="دریافت داده واقعی فروش">
            داده‌ها از فاکتورهای ثبت‌شده در صندوق فروشگاهی کسب‌وکارهای متصل به سرویس باشگاه مشتریان
            یا نظرسنجی اسمارت‌ایکس دریافت می‌شوند.
          </Step>
          <Step num={2} title="تجمیع و ناشناس‌سازی">
            اطلاعات کسب‌وکارها به‌صورت جداگانه نمایش داده نمی‌شود. داده‌ها برای ساخت تصویر کلی بازار،
            تجمیع و ناشناس می‌شوند.
          </Step>
          <Step num={3} title="محاسبه میانگین بازار">
            شاخص‌های فروش، سفارش، قیمت و سبد خرید برای  کسب‌وکارهای متصل بازار  محاسبه می‌شوند.
          </Step>
          <Step num={4} title="مقایسه شما با بازار">
            عملکرد شعبه شما کنار میانگین بازار قرار می‌گیرد و اختلاف آن به‌صورت ساده و رنگی نمایش
            داده می‌شود.
          </Step>
        </div>
      </Section>

      <Section>
        <H2>خروجی گزارش چطور خوانده می‌شود؟</H2>
        <p className="text-[#4b5563] leading-relaxed">
          برای هر شاخص، سه مقدار نمایش داده می‌شود: شعبه شما، بازار و اختلاف. رنگ‌ها کمک می‌کنند در
          یک نگاه وضعیت خود را بفهمید.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          <div className="p-4 rounded-[20px] text-center font-black bg-[#ecfdf3] text-[#039855] border border-[#abefc6]">
            جلوتر از بازار
          </div>
          <div className="p-4 rounded-[20px] text-center font-black bg-[#f2f4f7] text-[#475467] border border-[#d0d5dd]">
            هم‌تراز بازار
          </div>
          <div className="p-4 rounded-[20px] text-center font-black bg-[#fff1f3] text-[#d92d20] border border-[#fecdd6]">
            عقب‌تر از بازار
          </div>
        </div>
        <div className="mt-4 overflow-hidden rounded-[22px] border border-[#e7eaf0]">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13.2px] min-w-[520px]">
              <thead>
                <tr>
                  {['سناریو', 'شعبه شما', 'بازار', 'اختلاف', 'برداشت'].map((h) => (
                    <th
                      key={h}
                      className="px-2.5 py-3 text-center border-b border-[#e7eaf0] bg-[#f8fafc] text-[#344054] font-black"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-2.5 py-3 text-center border-b border-[#e7eaf0]">آرامش</td>
                  <td className="px-2.5 py-3 text-center border-b border-[#e7eaf0]">۵٪ افت</td>
                  <td className="px-2.5 py-3 text-center border-b border-[#e7eaf0]">۱۵٪ افت</td>
                  <td className="px-2.5 py-3 text-center border-b border-[#e7eaf0] text-[#039855] font-black">
                    ۱۰٪ بهتر
                  </td>
                  <td className="px-2.5 py-3 text-center border-b border-[#e7eaf0]">
                    بازار افت کرده، اما شما مقاوم‌تر بوده‌اید.
                  </td>
                </tr>
                <tr>
                  <td className="px-2.5 py-3 text-center">هشدار</td>
                  <td className="px-2.5 py-3 text-center">۱۰٪ افت</td>
                  <td className="px-2.5 py-3 text-center">۵٪ رشد</td>
                  <td className="px-2.5 py-3 text-center text-[#d92d20] font-black">۱۵٪ عقب‌تر</td>
                  <td className="px-2.5 py-3 text-center">بازار رشد کرده، اما شما عقب مانده‌اید.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <p className="mt-3 text-[#667085] text-sm">نمونه‌های بالا صرفاً برای توضیح مفهوم هستند.</p>
      </Section>

      <Section>
        <H2>بازار در این گزارش یعنی چه؟</H2>
        <p className="text-[#4b5563] leading-relaxed">
          در این گزارش، بازار به معنی میانگین عملکرد کافه‌ها و رستوران‌های متصل به اسمارت‌ایکس
          در سطح کشور است؛ کسب‌وکارهایی که داده فاکتور فروش آن‌ها از صندوق فروشگاهی به سیستم ارسال
          می‌شود.
        </p>
        <Note>
          در نسخه‌های بعدی، امکان دقیق‌تر شدن مقایسه بر اساس شهر، نوع کسب‌وکار و گروه‌های مشابه در
          مسیر توسعه قرار دارد.
        </Note>
      </Section>
    </ContentPage>

    {/* ── Page 03 ── */}
    <ContentPage>
      <Topline title="نبض بازار | اعتماد و محرمانگی" page="03" />
      <Section>
        <H2>چرا این گزارش قابل اتکاست؟</H2>
        <Cards>
          <InfoCard title="داده واقعی صندوق فروشگاهی">
            نبض بازار بر پایه فاکتورهای واقعی ثبت‌شده در صندوق ساخته می‌شود، نه نظرسنجی یا تخمین.
          </InfoCard>
          <InfoCard title="فراتر از فروش آنلاین">
            داده صندوق می‌تواند کانال‌های ثبت‌شده مثل حضوری، دلیوری، بیرون‌بر و آنلاین را پوشش دهد.
          </InfoCard>
          <InfoCard title="به‌روزرسانی روزانه">
            گزارش معمولاً با داده‌های فروش تا شب قبل به‌روزرسانی می‌شود.
          </InfoCard>
          <InfoCard title="ساخته‌شده برای تصمیم">
            هدف گزارش فقط نمایش عدد نیست؛ هدف فهم جایگاه شما نسبت به بازار است.
          </InfoCard>
        </Cards>
      </Section>

      <Section>
        <H2>حریم خصوصی چگونه حفظ می‌شود؟</H2>
        <p className="text-[#4b5563] leading-relaxed">
          نبض بازار برای نمایش تصویر کلی بازار طراحی شده است، نه برای نمایش اطلاعات یک کسب‌وکار خاص.
          فروش، نام، برند یا اطلاعات محرمانه هیچ کسب‌وکاری در خروجی بازار نمایش داده نمی‌شود.
        </p>
        <Quote>هیچ‌کس فروش هیچ‌کس را نمی‌بیند؛ اما همه تصویر بازار را می‌بینند.</Quote>
        <Cards cols={3}>
          <InfoCard title="تجمیع‌شده" soft>
            داده‌ها در سطح کلی بازار تحلیل می‌شوند.
          </InfoCard>
          <InfoCard title="ناشناس‌شده" soft>
            هویت کسب‌وکارها در خروجی بازار قابل مشاهده نیست.
          </InfoCard>
          <InfoCard title="قابل اعتماد" soft>
            هدف، مقایسه با بازار است نه افشای رقبا.
          </InfoCard>
        </Cards>
      </Section>

      <Section>
        <H2>چرا برای مشتریان فعال شده است؟</H2>
        <p className="text-[#4b5563] leading-relaxed">
          نبض بازار به‌عنوان یک ارزش افزوده برای مشتریان اسمارت‌ایکس فعال شده است. این
          نسخه، خروجی تحلیل‌شده و تجمیعی از  کسب‌وکارهای متصل بازار  را در اختیار شما قرار می‌دهد تا
          بتوانید جایگاه خود را نسبت به میانگین بازار بهتر درک کنید.
        </p>
        <p className="text-[#4b5563] leading-relaxed">
          این دسترسی به معنای ساده یا کم‌ارزش بودن گزارش نیست؛ بلکه بخشی از تعهد اسمارت‌ایکس برای
          افزایش ارزش سرویس فعلی و کمک به تصمیم‌گیری دقیق‌تر مشتریان است. نسخه‌های بعدی می‌توانند
          قابلیت‌های تکمیلی متفاوتی مانند تحلیل عمیق‌تر، پیش‌بینی، دستیار هوشمند و پیشنهاد اقدام
          داشته باشند.
        </p>
        <Quote>داده شما محرمانه می‌ماند؛ بینش بازار به شما برمی‌گردد.</Quote>
      </Section>
    </ContentPage>

    {/* ── Page 04 ── */}
    <ContentPage>
      <Topline title="نبض بازار | مسیر توسعه" page="04" />
      <Section>
        <H2>مسیر آینده نبض بازار</H2>
        <p className="text-[17px] text-[#344054] leading-relaxed">
          نبض بازار نقطه شروع است: نمایش جایگاه شما نسبت به بازار. اما مسیر آینده نبض بازار،
          تبدیل شدن به یک دستیار تصمیم‌ساز برای مدیران کافه و رستوران است.
        </p>
        <div className="grid gap-3.5 mt-4">
          {[
            {
              tag: 'گام ۱',
              title: 'مقایسه دقیق‌تر',
              desc: 'تفکیک بازار بر اساس شهر، نوع کسب‌وکار، رده قیمتی و کسب‌وکارهای هم‌رده.',
            },
            {
              tag: 'گام ۲',
              title: 'تحلیل علت',
              desc: 'تشخیص اینکه عقب‌ماندن یا جلو بودن از بازار بیشتر از فروش، سفارش، قیمت یا سبد خرید آمده است.',
            },
            {
              tag: 'گام ۳',
              title: 'پیشنهاد اقدام',
              desc: 'پیشنهاد کمپین، بازبینی قیمت، فروش مکمل یا اقدام مدیریتی بر اساس وضعیت شاخص‌ها.',
            },
            {
              tag: 'گام ۴',
              title: 'دستیار هوش مصنوعی',
              desc: 'پرسش و پاسخ مدیریتی روی داده‌ها؛ مثل «چرا از بازار عقب افتادم؟» یا «برای ماه بعد چه کنم؟»',
            },
          ].map((item) => (
            <div
              key={item.tag}
              className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-0 sm:gap-4 border border-[#e7eaf0] rounded-3xl overflow-hidden bg-white"
            >
              <div className="p-[18px] text-white font-black flex items-center justify-center text-center bg-gradient-to-br from-[#7c3aed] to-[#312e81]">
                {item.tag}
              </div>
              <div className="px-[18px] py-[17px]">
                <strong className="block mb-1 text-base text-[#101828]">{item.title}</strong>
                <p className="m-0 text-[#4b5563] text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <H2>از گزارش تا تصمیم</H2>
        <p className="text-[#4b5563] leading-relaxed">
          هدف نهایی نبض بازار این نیست که فقط بگوید چه اتفاقی افتاده است. مسیر توسعه این محصول به
          سمت پاسخ‌های کاربردی‌تر حرکت می‌کند:
        </p>
        <Cards>
          <InfoCard title="چه شد؟">نمایش فروش، سفارش، قیمت و سبد خرید نسبت به بازار.</InfoCard>
          <InfoCard title="چرا شد؟">تحلیل اینکه اختلاف با بازار از کدام شاخص آمده است.</InfoCard>
          <InfoCard title="چه می‌شود؟">پیش‌بینی روند احتمالی فروش، سفارش و بازار.</InfoCard>
          <InfoCard title="چه کار کنم؟">پیشنهاد اقدام عملی برای بهبود عملکرد.</InfoCard>
        </Cards>
      </Section>
    </ContentPage>

    {/* ── Page 05 ── */}
    <ContentPage>
      <Topline title="نبض بازار | نسخه‌ها و قابلیت‌ها" page="05" />
      <Section>
        <H2>نسخه پایه و مسیر نسخه‌های پیشرفته</H2>
        <p className="text-[#4b5563] leading-relaxed">
          نبض بازار برای مشتریان اسمارت‌ایکس فعال شده و امکان مقایسه اصلی با بازار را
          فراهم می‌کند. مسیر توسعه محصول به‌صورت مرحله‌ای ادامه پیدا می‌کند و در نسخه‌های بعدی،
          قابلیت‌های تکمیلی متفاوتی می‌توانند به محصول اضافه شوند؛ قابلیت‌هایی که فراتر از مشاهده
          جایگاه بازار هستند و روی تحلیل، پیش‌بینی و تصمیم‌سازی تمرکز دارند.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-4 mt-4">
          <div className="rounded-[28px] overflow-hidden border border-[#e7eaf0] bg-white">
            <div className="px-5 py-[18px] text-white bg-gradient-to-br from-[#111827] to-[#4c1d95]">
              <strong className="block text-xl mb-1">نبض بازار</strong>
              <span className="text-[#e9d5ff] text-[13px]">ارزش افزوده فعال برای مشتریان اسمارت‌ایکس</span>
            </div>
            <ul className="m-0 py-[18px] pr-8 pl-[18px] space-y-2">
              {[
                'مقایسه فروش، سفارش، قیمت و سبد با بازار',
                'نمایش اختلاف شعبه با میانگین بازار',
                'وضعیت جلوتر، هم‌تراز یا عقب‌تر از بازار',
                'جدول روند ماهانه',
                'داده‌های تجمیع‌شده و ناشناس',
              ].map((item) => (
                <li key={item} className="text-[#344054] text-sm leading-relaxed">
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-[28px] overflow-hidden border border-[#e7eaf0] bg-white">
            <div className="px-5 py-[18px] text-white bg-gradient-to-br from-[#4c1d95] to-[#7c3aed]">
              <strong className="block text-xl mb-1">قابلیت‌های تکمیلی آینده</strong>
              <span className="text-[#e9d5ff] text-[13px]">مسیر توسعه نسخه‌های بعدی</span>
            </div>
            <ul className="m-0 py-[18px] pr-8 pl-[18px] space-y-2">
              {[
                'دستیار هوش مصنوعی برای تحلیل مدیریتی',
                'پیشنهاد اقدام بر اساس وضعیت شاخص‌ها',
                'پیش‌بینی فروش و سفارش',
                'گزارش‌های خاص فروش و رفتار مشتری',
                'گزارش PDF ماهانه برای مالک یا سرمایه‌گذار',
                'تحلیل قیمت‌گذاری و سبد خرید',
              ].map((item) => (
                <li key={item} className="text-[#344054] text-sm leading-relaxed">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section>
        <H2>چه قابلیت‌هایی می‌توانند اضافه شوند؟</H2>
        <Cards>
          <InfoCard title="دستیار هوش مصنوعی">
            پاسخ به سؤال‌هایی مثل «چرا فروش من از بازار عقب افتاده؟» یا «اولویت اقدام من چیست؟»
          </InfoCard>
          <InfoCard title="گزارش‌های ویژه فروش">
            تحلیل بهترین روزها، افت کانال فروش، تغییر رفتار خرید و روندهای خاص.
          </InfoCard>
          <InfoCard title="پیشنهاد کمپین">
            اتصال گزارش به اقدام؛ مثل کمپین بازگشت مشتری یا فروش مکمل.
          </InfoCard>
          <InfoCard title="گزارش سرمایه‌گذار">
            خروجی مدیریتی برای نشان دادن عملکرد کسب‌وکار نسبت به بازار.
          </InfoCard>
        </Cards>
        <Quote>
          نبض بازار جایگاه شما را نسبت به بازار روشن می‌کند؛ نسخه‌های بعدی می‌توانند لایه‌های
          تکمیلی تحلیل، پیش‌بینی و اقدام را به این تجربه اضافه کنند.
        </Quote>
      </Section>
    </ContentPage>

    {/* ── Page 06 ── */}
    <ContentPage>
      <Topline title="نبض بازار | پرسش‌های پرتکرار" page="06" />
      <Section>
        <H2>پرسش‌های پرتکرار</H2>
        <div className="mt-2.5 border-t border-[#e7eaf0]">
          {[
            {
              q: 'آیا اطلاعات فروش من برای دیگران نمایش داده می‌شود؟',
              a: 'خیر. اطلاعات هیچ کسب‌وکاری به‌صورت جداگانه نمایش داده نمی‌شود. گزارش بر اساس داده‌های تجمیع‌شده و ناشناس ساخته می‌شود.',
            },
            {
              q: 'آیا می‌توانم فروش رقیبم را ببینم؟',
              a: 'خیر. نبض بازار برای مشاهده اطلاعات رقبا نیست؛ برای فهم جایگاه شما نسبت به میانگین بازار است.',
            },
            {
              q: 'داده‌ها از کجا می‌آیند؟',
              a: 'از فاکتورهای واقعی فروش که از صندوق فروشگاهی به سرویس باشگاه مشتریان یا نظرسنجی اسمارت‌ایکس ارسال می‌شوند.',
            },
            {
              q: 'آیا گزارش فقط فروش آنلاین را نشان می‌دهد؟',
              a: 'خیر. چون منبع داده صندوق فروشگاهی است، می‌تواند کانال‌های ثبت‌شده در صندوق مثل حضوری، دلیوری، بیرون‌بر و آنلاین را پوشش دهد.',
            },
            {
              q: 'گزارش چند وقت یک‌بار به‌روزرسانی می‌شود؟',
              a: 'نبض بازار به‌صورت روزانه به‌روزرسانی می‌شود و معمولاً داده‌ها تا شب قبل در گزارش لحاظ می‌شوند.',
            },
            {
              q: 'چرا برای مشتریان فعال شده است؟',
              a: 'این نسخه به‌عنوان ارزش افزوده برای مشتریان اسمارت‌ایکس فعال شده تا بتوانند خروجی تحلیل‌شده و تجمیعی بازار را در کنار گزارش‌های داخلی خود ببینند.',
            },
            {
              q: 'آیا نسخه‌های پیشرفته پولی خواهند بود؟',
              a: 'نبض بازار به‌عنوان ارزش افزوده برای مشتریان اسمارت‌ایکس فعال شده است. قابلیت‌های پیشرفته‌تر مانند تحلیل عمیق، دستیار هوشمند، پیش‌بینی و پیشنهاد اقدام می‌توانند در آینده به‌عنوان سرویس‌های تکمیلی ارائه شوند.',
            },
            {
              q: 'اگر از بازار عقب‌تر باشم یعنی عملکردم بد است؟',
              a: 'نه همیشه. این گزارش یک سیگنال مقایسه‌ای است و نشان می‌دهد کدام بخش نیاز به بررسی بیشتر دارد.',
            },
          ].map((item) => (
            <div key={item.q} className="py-4 border-b border-[#e7eaf0]">
              <strong className="block mb-1 text-[#101828]">{item.q}</strong>
              <p className="m-0 text-[#4b5563] text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <H2>جمع‌بندی</H2>
        <p className="text-[#4b5563] leading-relaxed">
          نبض بازار نقطه شروع یک مسیر بزرگ‌تر است: تبدیل داده‌های تحلیل‌شده فروش به بینش
          بازار. این نسخه به شما نشان می‌دهد نسبت به بازار کجا ایستاده‌اید؛ نسخه‌های آینده می‌توانند
          لایه‌های تکمیلی‌تری مانند تحلیل علت، پیش‌بینی، گزارش‌های تخصصی و پیشنهاد اقدام را به این
          تجربه اضافه کنند.
        </p>
        <div
          className="mt-6 px-6 py-[26px] rounded-[30px] text-white"
          style={{
            background:
              'radial-gradient(circle at 12% 20%, rgba(255,255,255,0.18), transparent 24%), linear-gradient(135deg, #111827, #5b21b6 70%, #7c3aed)',
          }}
        >
          <strong className="block text-2xl mb-2">نبض بازار؛ از مقایسه تا تصمیم</strong>
          <p className="m-0 text-[#ede9fe] text-base leading-relaxed">
            امروز جایگاه خود را نسبت به بازار ببینید. در نسخه‌های بعدی، با لایه‌های تکمیلی تحلیل،
            پیش‌بینی و اقدام، تصمیم‌های دقیق‌تری بگیرید.
          </p>
        </div>
      </Section>
    </ContentPage>
  </div>
);

export default MarketPulseProductDocContent;
